const MIN_GAP = 72;
const BOTTOM_NAV_RESERVE = 96;
const VIEW_MARGIN = 16;

function targetBox(target) {
  return {
    left: target.x,
    top: target.y,
    right: target.x + target.w,
    bottom: target.y + target.h,
  };
}

function boxesOverlap(a, b) {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function overlapArea(a, b) {
  const x = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const y = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return x * y;
}

function gapBetween(card, box) {
  if (boxesOverlap(card, box)) return -1;
  const dx = card.right < box.left ? box.left - card.right : box.right < card.left ? card.left - box.right : 0;
  const dy = card.bottom < box.top ? box.top - card.bottom : box.bottom < card.top ? card.top - box.bottom : 0;
  if (dx === 0) return dy;
  if (dy === 0) return dx;
  return Math.hypot(dx, dy);
}

function minGapToTargets(card, targets) {
  if (!targets.length) return Number.POSITIVE_INFINITY;
  return Math.min(...targets.map((t) => gapBetween(card, targetBox(t))));
}

function totalOverlap(card, targets) {
  return targets.reduce((sum, t) => sum + overlapArea(card, targetBox(t)), 0);
}

function clampPos(left, top, maxLeft, maxTop) {
  return {
    left: Math.min(maxLeft, Math.max(VIEW_MARGIN, left)),
    top: Math.min(maxTop, Math.max(VIEW_MARGIN, top)),
  };
}

function unionBox(targets) {
  if (!targets.length) return null;
  return targets.reduce(
    (acc, t) => {
      const box = targetBox(t);
      return {
        left: Math.min(acc.left, box.left),
        top: Math.min(acc.top, box.top),
        right: Math.max(acc.right, box.right),
        bottom: Math.max(acc.bottom, box.bottom),
      };
    },
    { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
  );
}

function cardFrom(pos, width, height) {
  return {
    left: pos.left,
    top: pos.top,
    right: pos.left + width,
    bottom: pos.top + height,
  };
}

function scoreCard(card, targets) {
  const overlap = totalOverlap(card, targets);
  const gap = minGapToTargets(card, targets);
  if (overlap > 0) return -1_000_000 + -overlap;
  const farEnough = gap >= MIN_GAP || gap === Number.POSITIVE_INFINITY;
  return gap + (farEnough ? 1000 : 0);
}

/** Pull the spotlight off the caption so the hole is never under Skip / Next. */
export function clipHighlightFromCaption(target, caption, pad = 12) {
  if (!target || !caption) return target;
  const box = targetBox(target);
  const cap = {
    left: caption.left - pad,
    top: caption.top - pad,
    right: caption.right + pad,
    bottom: caption.bottom + pad,
  };
  if (!boxesOverlap(box, cap)) return target;
  const leftovers = [
    { x: box.left, y: box.top, w: box.right - box.left, h: cap.top - box.top },
    { x: box.left, y: cap.bottom, w: box.right - box.left, h: box.bottom - cap.bottom },
    { x: box.left, y: box.top, w: cap.left - box.left, h: box.bottom - box.top },
    { x: cap.right, y: box.top, w: box.right - cap.right, h: box.bottom - box.top },
  ]
    .map((rect) => ({ ...rect, w: Math.max(0, rect.w), h: Math.max(0, rect.h) }))
    .filter((rect) => rect.w >= 32 && rect.h >= 32)
    .sort((a, b) => b.w * b.h - a.w * a.h);
  if (!leftovers.length) return target;
  const best = leftovers[0];
  return { ...target, x: best.x, y: best.y, w: best.w, h: best.h };
}

/**
 * Park the caption away from every highlighted control.
 * Never covers the hole when a clear strip of screen remains.
 */
export function placeCaptionCard(targets, { vw, vh, cardW, cardH } = {}) {
  const width = Math.min(cardW || 300, Math.max(160, (vw || 360) - VIEW_MARGIN * 2));
  const height = Math.max(120, cardH || 180);
  const viewW = vw || 360;
  const viewH = vh || 640;
  const maxLeft = Math.max(VIEW_MARGIN, viewW - width - VIEW_MARGIN);
  const maxTop = Math.max(VIEW_MARGIN, viewH - BOTTOM_NAV_RESERVE - height);
  const midX = Math.min(maxLeft, Math.max(VIEW_MARGIN, (viewW - width) / 2));
  const midY = Math.min(maxTop, Math.max(VIEW_MARGIN, (viewH - height) / 2));
  const list = Array.isArray(targets) ? targets.filter(Boolean) : [];
  const union = unionBox(list);

  const candidates = [
    { left: midX, top: VIEW_MARGIN },
    { left: maxLeft, top: VIEW_MARGIN },
    { left: VIEW_MARGIN, top: VIEW_MARGIN },
    { left: maxLeft, top: midY },
    { left: VIEW_MARGIN, top: midY },
    { left: midX, top: maxTop },
    { left: maxLeft, top: maxTop },
    { left: VIEW_MARGIN, top: maxTop },
  ];

  if (union) {
    candidates.push(
      clampPos(midX, union.top - height - MIN_GAP, maxLeft, maxTop),
      clampPos(midX, union.bottom + MIN_GAP, maxLeft, maxTop),
      clampPos(union.right + MIN_GAP, Math.max(VIEW_MARGIN, union.top), maxLeft, maxTop),
      clampPos(union.left - width - MIN_GAP, Math.max(VIEW_MARGIN, union.top), maxLeft, maxTop),
      clampPos(maxLeft, union.top - height - MIN_GAP, maxLeft, maxTop),
      clampPos(VIEW_MARGIN, union.top - height - MIN_GAP, maxLeft, maxTop)
    );
  }

  const xs = [VIEW_MARGIN, midX, maxLeft];
  const ys = [VIEW_MARGIN, midY, maxTop];
  for (const left of xs) {
    for (const top of ys) {
      candidates.push({ left, top });
    }
  }

  let best = candidates[0];
  let bestScore = Number.NEGATIVE_INFINITY;
  for (const pos of candidates) {
    const card = cardFrom(pos, width, height);
    const score = scoreCard(card, list);
    if (score > bestScore) {
      bestScore = score;
      best = pos;
    }
  }
  return best;
}
